/**
 * The step to resume on, from the index persisted for a previous visit.
 *
 * The hint is an index into the *visible* steps, and that list changes shape between
 * visits — alerts disappear when the company turns them off, payment disappears once
 * settled — so an index written against a longer list must clamp to the last step
 * rather than resolve to nothing. Left unclamped it dropped a customer who had
 * reached the confirmation back onto the first step.
 */
export const resolveResumeStepId = (
  visibleStepIds: string[],
  resumeIndex: number
): string | undefined => {
  if (!visibleStepIds.length) {
    return undefined;
  }

  const index = Math.min(Math.max(resumeIndex, 0), visibleStepIds.length - 1);

  return visibleStepIds[index];
};

interface ReachableStep {
  id: string;
  isVisible: boolean;
  isDisabled?: boolean;
}

/**
 * The step a "next" button on `fromStepId` would actually land on: the first later step
 * that is both visible and enabled.
 *
 * Enabled matters as much as visible. `nextStep` itself deliberately ignores it — a step
 * can still read as disabled while the refetch that unlocks it is in flight — but a
 * button offered to the customer has to lead somewhere usable, or they are advanced onto
 * a step the flow immediately sends them back off.
 */
export const nextReachableStepId = (
  steps: ReachableStep[],
  fromStepId: string
): string | undefined => {
  const from = steps.findIndex(step => step.id === fromStepId);

  if (from < 0) {
    return undefined;
  }

  return steps.slice(from + 1).find(step => step.isVisible && !step.isDisabled)
    ?.id;
};
